import { Directive, HostBinding, Input, OnChanges, OnInit, Optional, Self, SimpleChanges } from '@angular/core';
import { Tooltip } from 'primeng/tooltip';

/**
 * appActionTooltip
 * ----------------------------------------------------------------------------
 * Standardizes action-control tooltips app-wide: 2s hover delay, top placement,
 * body-append, shared style class. It COMPOSES PrimeNG's Tooltip (pTooltip) on
 * the SAME element: bind the text with [pTooltip]="..." and add appActionTooltip.
 *
 * For DISABLED-capable controls, put both on a wrapper <span> around the
 * <button>: a native disabled button emits no mouse events, so the wrapper
 * receives the hover (see span.action-tooltip-host CSS) and can show WHY the
 * action is disabled while the button stays genuinely disabled.
 *
 *   <span appActionTooltip
 *         [pTooltip]="row.disableEditProject ? row.disableEditProjectTitle : 'Edit Project'"
 *         [actionDisabled]="row.disableEditProject">
 *     <button class="btn btn-warning btn-sm" [disabled]="row.disableEditProject"
 *             (click)="editProject(row)"><i class="mdi mdi-pencil"></i></button>
 *   </span>
 */
@Directive({
  selector: '[appActionTooltip]',
  standalone: false,
})
export class ActionTooltipDirective implements OnInit, OnChanges {
  /** When true, marks the wrapper as disabled (cursor + aria). Tooltip still shows. */
  @Input() actionDisabled = false;

  /** Tooltip placement; defaults to 'top'. */
  @Input() actionTooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

  @Input() actionTooltipDelay = 250;

  @HostBinding('class.action-tooltip-host') readonly hostClass = true;

  @HostBinding('class.action-tooltip-host--disabled')
  get disabledClass(): boolean {
    return this.actionDisabled;
  }

  @HostBinding('attr.aria-disabled')
  get ariaDisabled(): string | null {
    return this.actionDisabled ? 'true' : null;
  }

  constructor(
    // Co-located PrimeNG Tooltip on the SAME element (present only when the
    // template also sets [pTooltip]). Optional so the directive degrades to
    // host classes only if the binding is missing.
    @Optional() @Self() private readonly tooltip: Tooltip,
  ) {}

  ngOnInit(): void {
    this.applyConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['actionTooltipPosition'] && !changes['actionTooltipPosition'].firstChange) ||
        (changes['actionTooltipDelay'] && !changes['actionTooltipDelay'].firstChange)) {
      this.applyConfig();
    }
  }

  /**
   * PrimeNG 16 reads tooltip config from _tooltipOptions via getOption();
   * template inputs write into it via ngOnChanges -> setOption. Programmatic
   * property assignment is ignored, so the public setOption() API is the only
   * correct write path here. setOption merges, so the [pTooltip] text updates
   * (setOption({tooltipLabel})) never clobber this static config.
   */
  private applyConfig(): void {
    this.tooltip?.setOption({
      showDelay: this.actionTooltipDelay,
      tooltipPosition: this.actionTooltipPosition,
      tooltipStyleClass: 'action-tooltip',
      appendTo: 'body',
    });
  }
}
